import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";

// 여기는 이제 실제 API 개발되고 나서 반영해야함 , 현재 모든 mdtr API 콜이 columns/ 로 되어있음
// 결과 계열
export interface MdtrSeries {
  sample: string;
  scores: number[];
}

export interface MdtrResult {
  workflow_type?: "mdtr" | "animal";
  chart_type?: "radar" | string;
  axes?: string[];
  threshold?: number;
  series?: MdtrSeries[];
  columnDefs?: any[];
  rowData?: any[];
  [key: string]: any;
}

export interface MdtrColumnListResponse {
  columns: string[];
  gene_count: number;
  sample_count: number;
  mdtr_coverage: number;
  mdtr_gene_count?: number;
  mdtr_matched_gene_count?: number;
}

export interface iMdtrState {
  result: MdtrResult | null;
  aiContext: Record<string, any> | null;
  resultList: any;
  resultByFileName: Record<string, any>;
  selectedTaskName: string | null;
  isLoading: boolean;
  isResultListLoading: boolean;
  isColumnsLoading: boolean;
  isAiContextLoading: boolean;
  columns: MdtrColumnListResponse | null;
  error: string | null;
  resultListError: string | null;
  columnsError: string | null;
  aiContextError: string | null;
}

interface MdtrResultParams {
  group_name: string;
  library_name: string;
  round?: number;
  step?: number;
  workflow_name?: string;
  task_name?: string;
  file_name?: string;
  extension?: string;
  use_backup?: boolean;
  timestamp?: string | null;
  workflow_type?: "mdtr" | string;
}

interface MdtrResultListParams {
  group_name: string;
  library_name: string;
  round?: number;
  step?: number;
  workflow_name?: string;
}

interface MdtrAiContextParams {
  group_name: string;
  library_name: string;
  round?: number;
  result_type?: "mdtr";
  use_backup?: boolean;
  timestamp?: string | null;
}

export const getMdtrResultListAPI = createAsyncThunk(
  "mdtr/getResultList",
  async (params: MdtrResultListParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/result-list/`,
        {
          round: 1,
          step: 1,
          workflow_name: "Drug Toxicity",
          ...params,
        },
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to load MDTR result list"
      );
    }
  }
);

// 결과 조회
export const getMdtrResultAPI = createAsyncThunk(
  "mdtr/getResult",
  async (params: MdtrResultParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const requestData: any = {
        round: 1,
        step: 1,
        workflow_name: "Drug Toxicity",
        workflow_type: "mdtr",
        ...params,
      };

      if (params.timestamp === null) delete requestData.timestamp;

      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/result/`,
        requestData,
        { withCredentials: true }
      );
      return {
        data: response.data as MdtrResult,
        fileName: params.file_name || null,
        taskName: params.task_name || null,
      };
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to load MDTR result"
      );
    }
  }
);

// 컬럼 목록 조회
export const getMdtrColumnListAPI = createAsyncThunk(
  "mdtr/getColumns",
  async (file: File, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/chemtox/mdtr/columns/`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      return response.data as MdtrColumnListResponse;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to parse MDTR columns"
      );
    }
  }
);

// workflow_chemtox_mdtr_round1의 MDTR_result.json과 선택적 XAI 자료 조회
export const getMdtrAiContextAPI = createAsyncThunk(
  "mdtr/getAiContext",
  async (params: MdtrAiContextParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/chemtox/mdtr/ai-context/`,
        {
          round: 1,
          result_type: "mdtr",
          ...params,
        },
        { withCredentials: true }
      );
      return response.data as Record<string, any>;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to load MDTR AI context"
      );
    }
  }
);

const initialState: iMdtrState = {
  result: null,
  aiContext: null,
  resultList: null,
  resultByFileName: {},
  selectedTaskName: null,
  isLoading: false,
  isResultListLoading: false,
  isColumnsLoading: false,
  isAiContextLoading: false,
  columns: null,
  error: null,
  resultListError: null,
  columnsError: null,
  aiContextError: null,
};

// 엠디티알 통신 상태
const mdtrSlice = createSlice({
  name: "mdtr",
  initialState,
  reducers: {
    clearMdtrResult: (state) => {
      state.result = null;
      state.resultByFileName = {};
      state.selectedTaskName = null;
      state.error = null;
      state.aiContext = null;
      state.aiContextError = null;
    },
    clearMdtrColumns: (state) => {
      state.columns = null;
      state.columnsError = null;
    },
    setMdtrColumns: (state, action: PayloadAction<MdtrColumnListResponse | null>) => {
      state.columns = action.payload;
      state.columnsError = null;
    },
    setSelectedMdtrResultTaskName: (state, action: PayloadAction<string | null>) => {
      state.selectedTaskName = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMdtrResultListAPI.pending, (state) => {
        state.isResultListLoading = true;
        state.resultListError = null;
      })
      .addCase(getMdtrResultListAPI.fulfilled, (state, action: PayloadAction<any>) => {
        state.isResultListLoading = false;
        state.resultList = action.payload;
      })
      .addCase(getMdtrResultListAPI.rejected, (state, action) => {
        state.isResultListLoading = false;
        state.resultListError = action.payload as string;
      })
      .addCase(getMdtrResultAPI.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMdtrResultAPI.fulfilled, (state, action: PayloadAction<{ data: MdtrResult; fileName: string | null; taskName: string | null }>) => {
        state.isLoading = false;
        const { data, fileName, taskName } = action.payload;
        state.result = data;
        if (taskName) state.selectedTaskName = taskName;
        if (fileName) {
          const compositeKey = taskName ? `${taskName}:${fileName}` : fileName;
          state.resultByFileName[compositeKey] = data;
        }
      })
      .addCase(getMdtrResultAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getMdtrColumnListAPI.pending, (state) => {
        state.isColumnsLoading = true;
        state.columnsError = null;
      })
      .addCase(getMdtrColumnListAPI.fulfilled, (state, action: PayloadAction<MdtrColumnListResponse>) => {
        state.isColumnsLoading = false;
        state.columns = action.payload;
      })
      .addCase(getMdtrColumnListAPI.rejected, (state, action) => {
        state.isColumnsLoading = false;
        state.columns = null;
        state.columnsError = action.payload as string;
      })
      .addCase(getMdtrAiContextAPI.pending, (state) => {
        state.isAiContextLoading = true;
        state.aiContextError = null;
      })
      .addCase(getMdtrAiContextAPI.fulfilled, (state, action: PayloadAction<Record<string, any>>) => {
        state.isAiContextLoading = false;
        state.aiContext = action.payload;
      })
      .addCase(getMdtrAiContextAPI.rejected, (state, action) => {
        state.isAiContextLoading = false;
        state.aiContext = null;
        state.aiContextError = action.payload as string;
      });
  },
});

export const { clearMdtrResult, clearMdtrColumns, setMdtrColumns, setSelectedMdtrResultTaskName } = mdtrSlice.actions;
export default mdtrSlice.reducer;
